import { CdkOverlayOrigin, Overlay } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { Component, Injector, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { CommonWorkflowStep, LoggingService, SessionStorageKeys } from "src/app/core";
import {
  EVENT_LOCAL_STORAGE_CONFIG,
  EventLocalStorageService,
  EventLocalStorageServiceConfig,
} from "src/app/features/events/shared/services/event-local-storage.service";
import { Showroom, ShowroomLoggingProp, ShowroomWorkflow, ShowroomWorkflowSource } from "../../../models";
import { ShowroomService } from "../../../services/showroom.service";
import { ShowroomShortlistOverlayInput } from "../../models/showroom-shortlist-overlay-input";
import { ShowroomShortlistOverlayRef } from "../../models/showroom-shortlist-overlay-ref";
import { COMPONENT_OVERLAY_REF, ShowroomShortlistService } from "../../services/showroom-shortlist.service";
import { ShowroomShortlistOverlayComponent } from "../showroom-shortlist-overlay/showroom-shortlist-overlay.component";

const Workflow = ShowroomWorkflow.Shortlist;
const WorkflowSource = ShowroomWorkflowSource.ShowroomShortlistTrigger;

@Component({
    selector: "app-showroom-shortlist-trigger",
    templateUrl: "./showroom-shortlist-trigger.component.html",
    styleUrls: ["./showroom-shortlist-trigger.component.scss"],
    providers: [
        {
            provide: EVENT_LOCAL_STORAGE_CONFIG,
            useValue: {
                shortlistKey: SessionStorageKeys.ShowroomShortlistSaveData,
            } as EventLocalStorageServiceConfig,
        },
        EventLocalStorageService,
        ShowroomShortlistService,
    ],
    standalone: false
})
export class ShowroomShortlistTriggerComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject();
  private _loggingData: {
    [ShowroomLoggingProp.ShowroomName]: string;
    [ShowroomLoggingProp.ShowroomOwnerName]: string;
    [ShowroomLoggingProp.EventId]: string;
  };
  private _variantSkus: number[];
  private _eventId: number;
  private _showroom: Showroom;
  private _overlayRefShowroomShortlist: ShowroomShortlistOverlayRef;

  constructor(
    private _overlay: Overlay,
    private _showroomService: ShowroomService,
    private _showroomShortlistService: ShowroomShortlistService,
    private _injector: Injector,
    private _loggingService: LoggingService,
  ) {}

  itemCount: number;

  @ViewChild(CdkOverlayOrigin) _shortlistTriggerOverlayOrigin: CdkOverlayOrigin;

  ngOnInit(): void {
    this._loggingService.logWorkflow(
      "Showroom shortlist trigger init",
      "ShowroomShortlistTriggerComponent.ngOnInit",
      Workflow,
      CommonWorkflowStep.ComponentLoad,
      WorkflowSource,
    );

    this._showroom = this._showroomService.currentShowroom();
    this._eventId = this._showroom.eventId;

    this._loggingData = {
      [ShowroomLoggingProp.EventId]: `${this._eventId}`,
      [ShowroomLoggingProp.ShowroomName]: this._showroom.name,
      [ShowroomLoggingProp.ShowroomOwnerName]: this._showroom.ownerName,
    };

    this._initData("ngOnInit");

    EventLocalStorageService.shortListUpdated$
      .pipe(
        tap(() => {
          this._initData("shortListUpdated$");
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onShortlistClick(): void {
    const overlayRef = this._overlay.create({
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._shortlistTriggerOverlayOrigin.elementRef)
        .withPositions([
          {
            originX: "end",
            originY: "bottom",
            overlayX: "end",
            overlayY: "top",
            offsetY: 7,
          },
        ]),
      scrollStrategy: this._overlay.scrollStrategies.block(),
      hasBackdrop: true,
      backdropClass: "cdk-overlay-transparent-backdrop",
    });

    this._overlayRefShowroomShortlist = new ShowroomShortlistOverlayRef(overlayRef);

    const injector = Injector.create({
      providers: [
        {
          provide: COMPONENT_OVERLAY_REF,
          useValue: this._overlayRefShowroomShortlist,
        },
        {
          provide: MAT_DIALOG_DATA,
          useValue: {
            eventId: this._eventId,
            variantSkus: this._variantSkus,
            showroom: this._showroom,
          } as ShowroomShortlistOverlayInput,
        },
      ],
      parent: this._injector,
    });

    const shortlistOverlayPortal = new ComponentPortal(ShowroomShortlistOverlayComponent, null, injector);
    overlayRef.attach(shortlistOverlayPortal);
  }

  private _initData(source: string): void {
    this._loggingService.logWorkflow(
      "Showroom shortlist trigger init data attempt",
      "ShowroomShortlistTriggerComponent._initData",
      Workflow,
      CommonWorkflowStep.ComponentLoadDataAttempt,
      WorkflowSource,
      {
        ...this._loggingData,
        InitDataEntryPoint: source,
      },
    );

    this._showroomShortlistService
      .getShortlistVariantSkus$(this._eventId)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: variantSkus => {
          this._variantSkus = variantSkus;
          this.itemCount = variantSkus.length;

          this._loggingService.logWorkflow(
            "Showroom shortlist trigger init data success",
            "ShowroomShortlistTriggerComponent._initData",
            Workflow,
            CommonWorkflowStep.ComponentLoadDataSuccess,
            WorkflowSource,
            {
              ...this._loggingData,
              ShortlistItemCount: `${this.itemCount}`,
            },
          );
        },
      });
  }
}
